/**
 * D-31: credit notes in the 2B that no purchase document matched.
 *
 * The owner's ruling is that only a matched credit note reduces credit, so
 * these reduce nothing in 4(A). That is the reason they must be SHOWN: the
 * credit each would have reversed is still counted, and a practitioner filing
 * the return is the one person who needs to know. The service carries the flag
 * on 4(A)'s provenance, which this screen otherwise prints only as one line of
 * "Source" behind a click — a flag on data nobody renders is not a flag.
 */

const HEAD_ORDER = ['igst', 'cgst', 'sgst', 'cess'] as const

export type UnmatchedCreditNotes = {
  count: number
  /** Only the heads that carry something, in return order. Paise, positive. */
  amounts: { head: (typeof HEAD_ORDER)[number]; paise: number }[]
}

/** The flag from a 4(A) cell, or null when there is nothing to say. */
export function unmatchedCreditNotes(
  cell: Record<string, unknown> | undefined,
): UnmatchedCreditNotes | null {
  const source = cell?.source as Record<string, unknown> | undefined
  const flag = source?.unmatched_credit_notes as Record<string, unknown> | undefined
  const count = typeof flag?.count === 'number' ? flag.count : 0
  if (count <= 0) return null

  const perHead = (flag?.per_head ?? {}) as Record<string, unknown>
  const amounts = HEAD_ORDER.flatMap((head) => {
    const value = perHead[head]
    // A magnitude by contract; `Math.abs` so a sign convention changing on the
    // service can never make this read as credit being added.
    return typeof value === 'number' && value !== 0
      ? [{ head, paise: Math.abs(value) }]
      : []
  })
  return { count, amounts }
}
